import type { HTMLAttributes, ReactNode } from 'react';
import { cx } from '@/lib/cx';

/*
 * The workhorse container. Structure comes from a hairline border, not a drop
 * shadow — elevation is reserved for things that genuinely float.
 *
 * Spreads the rest of its props so the `<section>` landmark can actually be
 * labelled (`aria-labelledby` pointing at its PanelHeader's `id`); a container
 * that cannot be named is a dead end for assistive tech.
 */
export interface PanelProps extends HTMLAttributes<HTMLElement> {
  children: ReactNode;
}

export function Panel({ className, children, ...rest }: PanelProps) {
  return (
    <section
      className={cx('rounded-lg border border-border bg-surface', className)}
      {...rest}
    >
      {children}
    </section>
  );
}

export function PanelHeader({
  title,
  description,
  actions,
  id,
  className,
}: {
  title: ReactNode;
  description?: ReactNode;
  actions?: ReactNode;
  id?: string;
  className?: string;
}) {
  return (
    <div
      className={cx(
        'flex flex-col gap-2 border-b border-border px-4 py-3 sm:flex-row sm:items-center sm:justify-between sm:gap-4',
        className,
      )}
    >
      <div className="min-w-0">
        <h2 id={id} className="truncate text-sm font-semibold text-fg">
          {title}
        </h2>
        {description && <p className="mt-0.5 text-xs text-fg-muted">{description}</p>}
      </div>
      {actions && <div className="flex shrink-0 items-center gap-2">{actions}</div>}
    </div>
  );
}

/*
 * Padding is a prop rather than something call sites override with `className`:
 * conflicting Tailwind utilities resolve by stylesheet order, not by the order
 * they appear in the attribute, so a `px-7` passed in can silently lose to the
 * `px-4` baked in here.
 */
const BODY_DENSITY = {
  /** Table/list density — the default, and what most panels want. */
  default: 'px-4 py-3',
  /** Roomier, for a focused single-purpose card such as sign-in. */
  comfortable: 'px-5 py-5 sm:px-7 sm:py-6',
  /** No padding, for a body that is entirely a table or list. */
  flush: '',
} as const;

export function PanelBody({
  density = 'default',
  className,
  children,
}: {
  density?: keyof typeof BODY_DENSITY;
  className?: string;
  children: ReactNode;
}) {
  return <div className={cx(BODY_DENSITY[density], className)}>{children}</div>;
}

export function PanelFooter({ className, children }: { className?: string; children: ReactNode }) {
  return (
    <div
      className={cx(
        'flex flex-col gap-3 border-t border-border px-4 py-3 sm:flex-row sm:items-center sm:justify-between',
        className,
      )}
    >
      {children}
    </div>
  );
}
